import type { ExplorerAbcCurve, AgeGroup, DetailedAgeGroup, RelationshipStatus } from "@/hooks/useBaseExplorer";

export interface AudienceFilter {
  ownerIds?: string[];
  speciesIds?: string[];
  breedIds?: string[];
  genderIds?: string[];
  sizeCategoryIds?: string[];
  coatTypeIds?: string[];
  petStatus?: "alive" | "deceased";
  abcCurves?: ExplorerAbcCurve[];
  daysSinceVisitMin?: number;
  daysSinceVisitMax?: number;
  hasWhatsApp?: boolean;
  ageGroups?: AgeGroup[];
  detailedAgeGroups?: DetailedAgeGroup[];
  ageYearsMin?: number;
  ageYearsMax?: number;
  relationshipStatuses?: RelationshipStatus[];
  minVisits?: number;
  minAverageTicket?: number;
  sterilized?: boolean;
  weightStatus?: "with" | "missing" | "stale";
}

export function applyAudienceFilter<
  T extends {
    hasWhatsApp: boolean;
    ownerId: string;
    abcCurve: ExplorerAbcCurve;
    daysSinceLastVisit: number | null;
    visitCount: number;
    averageTicket: number;
    relationshipStatus: RelationshipStatus;
    pets: {
      speciesId: string | null;
      breedId: string | null;
      genderId: string | null;
      sizeCategoryId: string | null;
      coatTypeId: string | null;
      ageGroup: AgeGroup;
      detailedAgeGroup: DetailedAgeGroup;
      ageYears: number | null;
      isSterilized: boolean | null;
      currentWeight: number | null;
      daysSinceWeightUpdate: number | null;
      isAlive: boolean;
    }[];
  }
>(rows: T[], filter: AudienceFilter): T[] {
  return rows.filter((row) => {
    if (filter.hasWhatsApp && !row.hasWhatsApp) return false;

    if (filter.ownerIds && filter.ownerIds.length > 0) {
      if (!filter.ownerIds.includes(row.ownerId)) return false;
    }

    if (filter.abcCurves && filter.abcCurves.length > 0) {
      if (!filter.abcCurves.includes(row.abcCurve)) return false;
    }

    if (filter.relationshipStatuses && filter.relationshipStatuses.length > 0) {
      if (!filter.relationshipStatuses.includes(row.relationshipStatus)) return false;
    }

    if (filter.minVisits !== undefined && row.visitCount < filter.minVisits) return false;
    if (filter.minAverageTicket !== undefined && row.averageTicket < filter.minAverageTicket) return false;

    if (filter.daysSinceVisitMin !== undefined && filter.daysSinceVisitMin > 0) {
      if (row.daysSinceLastVisit === null || row.daysSinceLastVisit < filter.daysSinceVisitMin)
        return false;
    }

    if (filter.daysSinceVisitMax !== undefined && filter.daysSinceVisitMax > 0) {
      if (row.daysSinceLastVisit === null || row.daysSinceLastVisit > filter.daysSinceVisitMax)
        return false;
    }

    if (filter.speciesIds && filter.speciesIds.length > 0) {
      const petSpecies = row.pets.filter((p) => p.isAlive).map((p) => p.speciesId);
      if (!filter.speciesIds.some((sid) => petSpecies.includes(sid))) return false;
    }

    if (filter.breedIds && filter.breedIds.length > 0) {
      const petBreeds = row.pets.filter((p) => p.isAlive).map((p) => p.breedId);
      if (!filter.breedIds.some((bid) => petBreeds.includes(bid))) return false;
    }

    if (filter.genderIds && filter.genderIds.length > 0) {
      const petGenders = row.pets.filter((p) => p.isAlive).map((p) => p.genderId);
      if (!filter.genderIds.some((gid) => petGenders.includes(gid))) return false;
    }

    if (filter.petStatus) {
      const hasMatchingStatus = row.pets.some((p) =>
        filter.petStatus === "alive" ? p.isAlive : !p.isAlive
      );
      if (!hasMatchingStatus) return false;
    }

    if (filter.sizeCategoryIds && filter.sizeCategoryIds.length > 0) {
      const petSizes = row.pets.filter((p) => p.isAlive).map((p) => p.sizeCategoryId);
      if (!filter.sizeCategoryIds.some((sid) => petSizes.includes(sid))) return false;
    }

    if (filter.coatTypeIds && filter.coatTypeIds.length > 0) {
      const petCoatTypes = row.pets.filter((p) => p.isAlive).map((p) => p.coatTypeId);
      if (!filter.coatTypeIds.some((cid) => petCoatTypes.includes(cid))) return false;
    }

    if (filter.ageGroups && filter.ageGroups.length > 0) {
      const petAgeGroups = row.pets.filter((p) => p.isAlive).map((p) => p.ageGroup);
      if (!filter.ageGroups.some((ag) => petAgeGroups.includes(ag))) return false;
    }

    if (filter.detailedAgeGroups && filter.detailedAgeGroups.length > 0) {
      const petAgeGroups = row.pets.filter((p) => p.isAlive).map((p) => p.detailedAgeGroup);
      if (!filter.detailedAgeGroups.some((ag) => petAgeGroups.includes(ag))) return false;
    }

    if (
      (filter.ageYearsMin !== undefined && filter.ageYearsMin >= 0) ||
      (filter.ageYearsMax !== undefined && filter.ageYearsMax >= 0)
    ) {
      const hasPetInRange = row.pets
        .filter((p) => p.isAlive)
        .some((p) => {
          if (p.ageYears === null) return false;
          if (filter.ageYearsMin !== undefined && p.ageYears < filter.ageYearsMin) return false;
          if (filter.ageYearsMax !== undefined && p.ageYears > filter.ageYearsMax) return false;
          return true;
        });
      if (!hasPetInRange) return false;
    }

    if (filter.sterilized !== undefined) {
      const hasMatchingSterilization = row.pets
        .filter((p) => p.isAlive)
        .some((p) => filter.sterilized ? p.isSterilized === true : p.isSterilized !== true);
      if (!hasMatchingSterilization) return false;
    }

    if (filter.weightStatus) {
      const hasMatchingWeight = row.pets
        .filter((p) => p.isAlive)
        .some((p) => {
          if (filter.weightStatus === "with") return p.currentWeight !== null;
          if (filter.weightStatus === "missing") return p.currentWeight === null;
          return p.daysSinceWeightUpdate === null || p.daysSinceWeightUpdate >= 180;
        });
      if (!hasMatchingWeight) return false;
    }

    return true;
  });
}
